import './screens.css';
import { exchange, readFacts } from '../sim/trashTalk.js';
import { whatTheyFight } from '../sim/ladder.js';
import { needs } from './contract.js';
import { capital, roomFor } from './venueWords.js';
import { fitOf } from '../sim/bank.js';

/**
 * The walkout: who these two are, before the bell.
 *
 * A fight has had a ranked opponent since step 50 and there has been nowhere to
 * *see* it. The ladder, the record, the gap to the card and whether this one is
 * for it were all readable in the debug panel, which is a debug panel — it is
 * the developer's window on the machine, not the game telling the player where
 * they stand.
 *
 * This holds the round while it is up. The intro is 1.2 seconds, which is long
 * enough to watch two people square up and nowhere near long enough to read who
 * they are, so `match.hold()` keeps them on their marks until somebody says go.
 *
 * Reads the campaign and the ladder and decides nothing itself. Even the talking
 * is somebody else's rule: `sim/trashTalk.js` turns the state into a line and
 * this renders it.
 */
export function createWalkout({
  campaign,
  match,
  bank = null,
  onWear = () => {},
  /**
   * A promise for "the booked fighter is ready to fight" — their body, which
   * arrives asynchronously. The bell waits for it, and says so. Optional: no
   * promise is a walkout that is always ready.
   */
  ready = null,
  /**
   * Who is fighting in a stand-in because their body never arrived — a line,
   * or null when everybody is in their own (step 96). Said on the walkout,
   * before the bell, rather than discovered in the ring.
   */
  standIns = () => null,
  container = document.body,
}) {
  needs(campaign, ['next', 'gate', 'against', 'wins', 'losses', 'ladder'], {
    who: 'the walkout',
    what: 'campaign',
  });
  needs(campaign.ladder, ['player', 'size'], { who: 'the walkout', what: 'campaign.ladder' });
  needs(match, ['hold', 'start'], { who: 'the walkout', what: 'match' });
  const root = el('div', 'screen');
  root.hidden = true;

  const kind = text('div', 'screen__kind', '');
  root.append(kind);

  // Where it is fought, and whose room that is. Under the kind, because the
  // room is part of what kind of night it is.
  const venue = el('div', 'screen__venue');
  const venueName = text('div', 'screen__venue-name', '');
  const venueStake = text('div', 'screen__venue-stake', '');
  venue.append(venueName, venueStake);
  root.append(venue);

  const card = el('div', 'screen__card');
  const you = corner('player');
  const versus = text('div', 'screen__versus', 'vs');
  const them = corner('opponent');
  card.append(you.root, versus, them.root);
  root.append(card);

  // The crowd and the player's look, said before they commit: how many of the
  // last few fights it has seen this exact fit in, what that leaves of it,
  // and — when it has gone stale — the things they own to change into.
  const wardrobe = el('div', 'screen__wardrobe');
  const boredLine = text('div', 'screen__bored', '');
  const swaps = el('div', 'screen__swaps');
  wardrobe.append(boredLine, swaps);
  root.append(wardrobe);

  const talk = el('div', 'screen__talk');
  const theirLine = line('opponent');
  const yourLine = line('player');
  talk.append(theirLine.root, yourLine.root);
  root.append(talk);

  const purse = text('div', 'screen__purse', '');
  root.append(purse);

  const standing = el('div', 'screen__standing');
  const place = el('div');
  const toGo = el('div');
  standing.append(place, toGo);
  root.append(standing);

  // A body that never arrived, said before the bell (step 96).
  const notice = text('div', 'screen__notice', '');
  notice.hidden = true;
  root.append(notice);

  const go = el('button', 'screen__go');
  go.textContent = 'fight';
  root.append(go);
  root.append(text('div', 'screen__hint', 'enter, space or click'));

  container.append(root);

  let up = false;
  /** The player's corner as last drawn, so a change of clothes redraws only the fit. */
  let lastYou = null;

  function show() {
    const next = campaign.next;
    const opponent = next.opponent;
    if (!opponent) return false;

    const act = next.kind === 'act';
    root.dataset.act = String(act);
    root.dataset.contender = String(campaign.gate.open);
    setText(kind, act ? `${next.name} — for the card` : 'circuit fight');

    const player = campaign.ladder.player;
    const head = campaign.against(opponent.id);

    lastYou = {
      name: player.name,
      rank: player.rank,
      borough: player.borough,
      styles: whatTheyFight(player, { atTheWalkout: true }).styles,
      record: `${campaign.wins}W ${campaign.losses}L`,
      home: player.home ? campaign.ladder.venue(player.home)?.name : null,
      fit: fitOf(bank?.worn ?? {}),
      // The player's own head-to-head is the same ledger read the other way.
      head: head.met ? `${head.wins}–${head.losses} against them` : 'never met',
      tier: null,
    };
    you.fill(lastYou);
    them.fill({
      name: opponent.name,
      rank: opponent.rank,
      borough: opponent.borough,
      // This is the walkout, which is where a fighter stops being a rumour.
      // Junior's styles are concealed on the offer screen and revealed here,
      // and `whatTheyFight` is the rule — the screen only says which screen it
      // is.
      styles: whatTheyFight(opponent, { atTheWalkout: true }).styles,
      record: `${opponent.record.wins}W ${opponent.record.losses}L`,
      home: opponent.home ? campaign.ladder.venue(opponent.home)?.name : null,
      fit: fitOf(opponent.gear ?? {}),
      head: head.met ? `${head.losses}–${head.wins} against you` : 'never met',
      tier: opponent.tier,
    });

    const room = roomFor(next.venue, { ladder: campaign.ladder, opponentId: opponent.id });
    venue.hidden = !room;
    venue.dataset.stake = room?.stake ?? 'none';
    setText(venueName, room ? `at ${room.name} — ${room.where}` : '');
    setText(venueStake, room ? capital(room.words) : '');

    drawWardrobe();

    const both = exchange(readFacts({ campaign, opponent, act }));
    theirLine.fill(opponent.name, both.opponent?.say);
    yourLine.fill(player.name, both.player?.say);

    // What the card is worth. The offer screen said it; this is the last time
    // it is said before somebody has to go and earn it.
    setText(purse, next.purse ? `purse $${next.purse.toLocaleString()}` : '');

    const gate = campaign.gate;
    setHTML(place, `rank <strong>${gate.rank}</strong> of ${campaign.ladder.size}`);
    setHTML(
      toGo,
      gate.open
        ? '<strong>contender</strong> — the card is open'
        : `<strong>${gate.short}</strong> place${gate.short === 1 ? '' : 's'} off the card (top ${gate.need})`
    );

    root.hidden = false;
    up = true;
    waitForTheBody();
    // Held rather than timed: the screen decides when the round starts, and
    // `match.start()` remains the door a headless run goes through.
    match.hold(true);
    return true;
  }

  /**
   * The player's look against the crowd's memory. Hidden for a player wearing
   * nothing — there is nothing for anybody to be bored of — and for a game
   * with no bank behind it.
   */
  function drawWardrobe() {
    const look = bank?.boredom;
    const worth = bank?.charisma?.();
    wardrobe.hidden = !look || !(worth?.bonus > 0);
    if (wardrobe.hidden) return;
    wardrobe.dataset.stale = String(look.stale);
    setText(
      boredLine,
      look.stale
        ? `the crowd has seen this fit in ${look.seen} of your last ${look.memory} fights — it is worth ` +
            `${Math.round(look.freshness * 100)}% to them tonight. Change something before the bell.`
        : look.seen
          ? `seen in ${look.seen} of your last ${look.memory} fights · still fresh to them · charisma ×${worth.scale.toFixed(2)}`
          : `a fit the crowd has not seen · charisma ×${worth.scale.toFixed(2)}`
    );
    // Only when it has gone stale: what they own, slot by slot, to change into.
    swaps.replaceChildren();
    if (!look.stale) return;
    // One change a slot, not the whole wardrobe: the nearest owned tier to the
    // one being worn (or the best owned, for an empty slot), and taking off
    // what is on. At the top of the shop that is twelve buttons, not forty.
    for (const slot of bank.catalogue) {
      const on = slot.tiers.find((one) => one.worn);
      const owned = slot.tiers.filter((one) => one.owned && !one.worn);
      const nearest = on
        ? owned.sort((a, b) => Math.abs(a.index - on.index) - Math.abs(b.index - on.index) || b.index - a.index)[0]
        : owned.at(-1);
      for (const tier of nearest ? [nearest] : []) {
        const swap = el('button', 'screen__swap');
        swap.textContent = `${slot.name.toLowerCase()}: ${tier.name}`;
        swap.addEventListener('click', () => {
          if (!bank.wear(slot.id, tier.id)?.ok) return;
          onWear({ slot: slot.id, tier: tier.id });
          you.fill({ ...lastYou, fit: fitOf(bank.worn) });
          drawWardrobe();
        });
        swaps.append(swap);
      }
      // Taking something off is a change too, and always there to make.
      if (on) {
        const off = el('button', 'screen__swap');
        off.dataset.off = 'true';
        off.textContent = `take off ${bare(on.name)}`;
        off.addEventListener('click', () => {
          if (!bank.wear(slot.id, null)?.ok) return;
          onWear({ slot: slot.id, tier: null });
          you.fill({ ...lastYou, fit: fitOf(bank.worn) });
          drawWardrobe();
        });
        swaps.append(off);
      }
    }
    if (!swaps.children.length) swaps.append(text('span', 'screen__swap-none', 'nothing else in the wardrobe — buy something on the hub'));
  }

  /** Whether the bell can go: false while the booked body is still arriving. */
  let fit = true;
  let waiting = 0;
  function sayStandIns() {
    const words = standIns?.() ?? null;
    notice.hidden = !words;
    setText(notice, words ?? '');
  }

  function waitForTheBody() {
    sayStandIns();
    const pending = ready?.();
    if (!pending?.then) {
      fit = true;
      go.disabled = false;
      setText(go, 'fight');
      return;
    }
    const token = ++waiting;
    fit = false;
    go.disabled = true;
    setText(go, 'warming up…');
    const done = () => {
      if (token !== waiting) return;
      sayStandIns();
      fit = true;
      go.disabled = false;
      setText(go, 'fight');
    };
    pending.then(done, done);
  }

  function dismiss() {
    if (!up || !fit) return false;
    up = false;
    root.hidden = true;
    match.start();
    return true;
  }

  function onKey(event) {
    if (!up) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    dismiss();
  }

  go.addEventListener('click', dismiss);
  window.addEventListener('keydown', onKey);

  return {
    element: root,
    get up() {
      return up;
    },
    show,
    dismiss,
    dispose() {
      window.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
}

/** "the grill people photograph" for any name, whether or not it brought its own article. */
const bare = (name) => (/^(the|a|an) /i.test(name) ? name[0].toLowerCase() + name.slice(1) : `the ${name.toLowerCase()}`);

/** One fighter's corner of the card. */
function corner(side) {
  const root = el('div', `corner corner--${side}`);
  const name = text('div', 'corner__name', '');
  const rank = text('div', 'corner__rank', '');
  const from = text('div', 'corner__from', '');
  const styles = text('div', 'corner__meta', '');
  const record = text('div', 'corner__record', '');
  const head = text('div', 'corner__head', '');
  // The fit, sold: what they walk out in, what they show, who is in their
  // corner — the slots a camera cannot see, named — and then what is on the
  // hands, feet and shins, which the camera can.
  const fit = el('div', 'corner__fit');
  root.append(name, rank, from, styles, record, head, fit);
  return {
    root,
    fill(who) {
      setText(name, who.name);
      setText(rank, `rank ${who.rank}${who.tier ? ` · ${who.tier}` : ''}`);
      // Where they are from, and the room they fight out of. The player has
      // none until they take one, and the card says so by saying nothing.
      setText(
        from,
        [who.borough ? `out of ${who.borough}` : '', who.home ? `fights out of ${who.home}` : '']
          .filter(Boolean)
          .join(' · ')
      );
      setText(styles, (who.styles ?? []).join(' + '));
      setText(record, who.record);
      setText(head, who.head);
      const pieces = who.fit ?? [];
      const worth = pieces.reduce((sum, piece) => sum + piece.tier.cost, 0);
      fit.replaceChildren(
        ...pieces
          .filter((piece) => piece.shows === 'walkout')
          .map((piece) => {
            const row = el('div', 'corner__piece');
            row.dataset.slot = piece.slot;
            row.append(text('span', 'corner__piece-slot', piece.name.toLowerCase()), text('span', 'corner__piece-name', piece.tier.name));
            return row;
          })
      );
      const body = pieces.filter((piece) => piece.shows === 'body');
      if (body.length) {
        fit.append(text('div', 'corner__body', body.map((piece) => piece.tier.name).join(' · ')));
      }
      fit.append(text('div', 'corner__worth', pieces.length ? `a $${worth.toLocaleString()} fit` : 'nothing bought, nothing worn'));
    },
  };
}

/** One thing somebody says. */
function line(side) {
  const root = el('div', `line line--${side}`);
  const who = text('span', 'line__who', '');
  const say = text('span', '', '');
  root.append(who, say);
  return {
    root,
    fill(name, words) {
      root.hidden = !words;
      setText(who, name ?? '');
      setText(say, words ?? '');
    },
  };
}

export function el(tag, className) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

export function text(tag, className, value) {
  const node = el(tag, className);
  node.textContent = value;
  return node;
}

export function setText(node, value) {
  if (node.textContent !== value) node.textContent = value;
}

/** Only ever fed strings this module built, and never anything a fighter named. */
export function setHTML(node, value) {
  if (node.innerHTML !== value) node.innerHTML = value;
}
